import React, { useEffect, useRef, useState } from "react";
import BpmnModeler from "bpmn-js/lib/Modeler";
import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn.css";
import api from "../../lib/api";
import { toast } from "sonner";
import { X, Save } from "lucide-react";

export default function BpmnEditorModal({ open, clientId, streamId, streamName, onClose, onSaved }) {
  const canvasRef = useRef(null);
  const modelerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    if (!open || !clientId || !streamId) return;
    let cancelled = false;
    setLoading(true);
    const modeler = new BpmnModeler({ container: canvasRef.current });
    modelerRef.current = modeler;
    (async () => {
      try {
        const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/map/${streamId}`);
        if (cancelled) return;
        await modeler.importXML(data.bpmn);
        modeler.get("canvas").zoom("fit-viewport");
        setEdited(data.edited);
      } catch (e) {
        toast.error("Could not load the process map");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; try { modeler.destroy(); } catch { /* noop */ } };
  }, [open, clientId, streamId]);

  const save = async () => {
    setSaving(true);
    try {
      const { xml } = await modelerRef.current.saveXML({ format: true });
      await api.put(`/admin/enterprise360/clients/${clientId}/map/${streamId}`, { bpmn: xml });
      toast.success("Process map saved");
      setEdited(true);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Could not save the map");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div data-testid="bpmn-editor-modal" className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--e360-primary)]">Edit process map · consultant edit-before-finalise</div>
            <div className="font-semibold text-black">{streamName} {edited && <span className="text-xs text-emerald-600">· edited</span>}</div>
          </div>
          <div className="flex gap-2">
            <button data-testid="bpmn-save" onClick={save} disabled={saving || loading}
              className="bg-[var(--e360-primary)] text-white px-4 py-2 text-[12px] tracking-[0.14em] uppercase flex items-center gap-1 disabled:opacity-50">
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
            <button data-testid="bpmn-close" onClick={onClose} className="border border-zinc-300 px-3 py-2 text-zinc-600"><X size={16} /></button>
          </div>
        </div>
        <div className="relative flex-1">
          {loading && <div className="absolute inset-0 flex items-center justify-center text-zinc-400 z-10">Loading map…</div>}
          <div ref={canvasRef} className="absolute inset-0 bg-white" />
        </div>
      </div>
    </div>
  );
}
